import * as XLSX from "xlsx";
import { todayStr } from "./finance";

export function buildWorkbook(data) {
  const { entries, clients, pipeline, invoices, businessProfile } = data;
  const wb = XLSX.utils.book_new();

  const entriesRows = entries.slice().sort((a, b) => a.date.localeCompare(b.date)).map((e) => ({
    Date: e.date, Type: e.type, Category: e.category, Description: e.description,
    Amount: e.amount, ClientID: e.clientId || "",
  }));
  XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(entriesRows), "Entries");

  const clientRows = clients.map((c) => ({ ClientID: c.id, Name: c.name, CreatedDate: c.createdDate, Balance: c.balance || 0 }));
  XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(clientRows), "Clients");

  const txnRows = [];
  clients.forEach((c) => (c.transactions || []).forEach((t) => txnRows.push({
    ClientID: c.id, ClientName: c.name, Date: t.date, Type: t.type, Amount: t.amount,
    PlanAmount: t.planAmount || "", Duration: t.duration || "", Note: t.note || "",
  })));
  XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(txnRows), "ClientTransactions");

  const pipelineRows = pipeline.map((p) => ({
    Brand: p.brand, ExpectedAmount: p.expectedAmount || 0, ExpectedDate: p.expectedDate || "",
    Status: p.status || "Pipeline", Notes: p.notes || "", CreatedDate: p.createdDate || "",
  }));
  XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(pipelineRows), "Pipeline");

  const invoiceRows = [];
  invoices.forEach((inv) => (inv.items || [{ description: "", price: 0, qty: 0, discount: 0, igstRate: 0 }]).forEach((item, idx) => invoiceRows.push({
    InvoiceNumber: inv.number, BillTo: inv.billTo, BillToAddress: inv.billToAddress || "",
    BillToGST: inv.billToGst || "", Date: inv.date, DueDate: inv.dueDate || "",
    ItemDescription: item.description, Price: item.price, Qty: item.qty,
    Discount: item.discount, IGSTRate: item.igstRate, InvoiceTotal: idx === 0 ? inv.total : "",
  })));
  XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(invoiceRows), "Invoices");

  XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet([businessProfile || {}]), "BusinessProfile");
  return wb;
}

export function exportToExcel(data) {
  const wb = buildWorkbook(data);
  XLSX.writeFile(wb, `LeanByPriyanka_Ledger_${todayStr()}.xlsx`);
}

function uid() { return Date.now().toString(36) + Math.random().toString(36).slice(2, 7); }

export function parseWorkbook(arrayBuffer) {
  const wb = XLSX.read(new Uint8Array(arrayBuffer), { type: "array" });
  const sheet = (n) => wb.Sheets[n];
  if (!sheet("Entries")) throw new Error('No "Entries" sheet found.');

  const entries = XLSX.utils.sheet_to_json(sheet("Entries")).map((r) => ({
    id: uid(), type: r.Type, category: r.Category, description: r.Description,
    amount: Number(r.Amount) || 0, date: String(r.Date).slice(0, 10),
    ...(r.ClientID ? { clientId: r.ClientID } : {}),
  }));

  const impClients = sheet("Clients") ? XLSX.utils.sheet_to_json(sheet("Clients")) : [];
  const impTxns = sheet("ClientTransactions") ? XLSX.utils.sheet_to_json(sheet("ClientTransactions")) : [];
  const clients = impClients.map((c) => ({
    id: c.ClientID || uid(), name: c.Name, createdDate: String(c.CreatedDate).slice(0, 10), balance: Number(c.Balance) || 0,
    transactions: impTxns.filter((t) => t.ClientID === c.ClientID).map((t) => ({
      id: uid(), date: String(t.Date).slice(0, 10), type: t.Type, amount: Number(t.Amount) || 0,
      planAmount: t.PlanAmount ? Number(t.PlanAmount) : undefined,
      duration: t.Duration ? Number(t.Duration) : undefined, note: t.Note || "",
    })),
  }));

  const pipeline = (sheet("Pipeline") ? XLSX.utils.sheet_to_json(sheet("Pipeline")) : []).map((p) => ({
    id: uid(), brand: p.Brand, expectedAmount: Number(p.ExpectedAmount) || 0,
    expectedDate: p.ExpectedDate ? String(p.ExpectedDate).slice(0, 10) : "",
    status: p.Status || "Pipeline", notes: p.Notes || "",
    createdDate: p.CreatedDate ? String(p.CreatedDate).slice(0, 10) : todayStr(),
  }));

  const invoiceGroups = {};
  (sheet("Invoices") ? XLSX.utils.sheet_to_json(sheet("Invoices")) : []).forEach((r) => {
    const key = String(r.InvoiceNumber);
    if (!invoiceGroups[key]) invoiceGroups[key] = {
      id: uid(), number: key, billTo: r.BillTo, billToAddress: r.BillToAddress || "",
      billToGst: r.BillToGST || "", date: String(r.Date).slice(0, 10),
      dueDate: r.DueDate ? String(r.DueDate).slice(0, 10) : "", items: [], total: Number(r.InvoiceTotal) || 0,
    };
    invoiceGroups[key].items.push({
      description: r.ItemDescription || "", price: Number(r.Price) || 0, qty: Number(r.Qty) || 0,
      discount: Number(r.Discount) || 0, igstRate: Number(r.IGSTRate) || 0,
    });
    if (r.InvoiceTotal) invoiceGroups[key].total = Number(r.InvoiceTotal);
  });
  const invoices = Object.values(invoiceGroups);

  let businessProfile = {};
  if (sheet("BusinessProfile")) {
    const b = XLSX.utils.sheet_to_json(sheet("BusinessProfile"));
    if (b[0]) businessProfile = b[0];
  }
  return { entries, clients, pipeline, invoices, businessProfile };
}
